import { Icon } from '@cbhq/cds-web/icons/Icon';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TableRow, TableCell } from '@cbhq/cds-web/tables';
import { TextBody, TextHeadline, TextLabel1, TextLabel2 } from '@cbhq/cds-web/typography';
import { memo, useCallback } from 'react';
import { DisplayType, format, formatTickPrice } from '@/utils/numberFormatter';
import { Bound } from '@/utils/types';
import { PoolsListTokensMedia } from '@/components/Pools/PoolsListTokensMedia';
import { USDCRewardsChip } from '@/components/common/USDCRewardsChip';
import { useDerivedPositionData } from './useDerivedPositionData';
import type { PositionDetails } from '@/onchain/hooks/usePositions';

type PortfolioPositionRowProps = {
  position: PositionDetails;
  testId?: string;
  onSelect: (positionId: bigint, positionManagerAddress?: `0x${string}`) => void;
};

/**
 * PortfolioPositionDesktopRow - Desktop table row view for a single portfolio position
 *
 * Displays position information in a table row format with columns for:
 * - Position (token pair, fee tier, rewards badge)
 * - Fees (unclaimed fees value)
 * - Liquidity (total liquidity value)
 * - Range (price range or "Full range")
 * - Status (in range / out of range indicator)
 */
export const PortfolioPositionDesktopRow = memo(function PortfolioPositionDesktopRow({
  position,
  testId,
  onSelect,
}: PortfolioPositionRowProps) {
  const {
    positionTokens,
    tickAtLimit,
    tickInRange,
    feePercent,
    totalUnclaimedFeesNotional,
    totalLiquidityNotional,
  } = position;
  const { baseToken, quoteToken, tokenPair, tokenConversionLabel, priceLower, priceUpper } =
    useDerivedPositionData(position);
  const hasUSDC = positionTokens.some((token) => token.symbol === 'USDC');

  const handleRowClick = useCallback(() => {
    onSelect(position.positionId, position.positionManagerAddress);
  }, [onSelect, position.positionId, position.positionManagerAddress]);

  return (
    <TableRow onClick={handleRowClick} testID={testId}>
      <TableCell>
        <HStack alignItems="center" gap={1}>
          <PoolsListTokensMedia size="xl" assets={[baseToken.asset, quoteToken.asset]} />
          <VStack gap={0.5} alignItems="flex-start">
            <TextHeadline as="span">{tokenPair}</TextHeadline>
            <HStack gap={0.5}>
              <TextLabel1
                borderRadius={200}
                paddingX={1}
                paddingY={0.5}
                background="bgAlternate"
                as="span"
              >
                {format({ value: feePercent, type: DisplayType.Percentage })}
              </TextLabel1>
              {hasUSDC && <USDCRewardsChip />}
            </HStack>
          </VStack>
        </HStack>
      </TableCell>
      <TableCell>
        <TextBody as="span" noWrap>
          {format({ value: totalUnclaimedFeesNotional, type: DisplayType.Fiat })}
        </TextBody>
      </TableCell>
      <TableCell>
        <TextBody as="span" noWrap>
          {format({ value: totalLiquidityNotional, type: DisplayType.Fiat })}
        </TextBody>
      </TableCell>
      <TableCell>
        {tickAtLimit.LOWER && tickAtLimit.UPPER ? (
          <TextBody as="span" noWrap>
            Full range
          </TextBody>
        ) : (
          <VStack gap={0}>
            <TextBody as="span" noWrap>
              {formatTickPrice({
                price: priceLower,
                atLimit: tickAtLimit,
                direction: Bound.LOWER,
              })}{' '}
              to{' '}
              {formatTickPrice({
                price: priceUpper,
                atLimit: tickAtLimit,
                direction: Bound.UPPER,
              })}
            </TextBody>
            <TextLabel2 as="span" color="fgMuted" noWrap>
              {tokenConversionLabel}
            </TextLabel2>
          </VStack>
        )}
      </TableCell>
      <TableCell>
        <HStack alignItems="center" gap={0.5}>
          <Icon
            name="dot"
            size="xs"
            active
            color={tickInRange ? 'fgPositive' : 'fgNegative'}
            aria-label={tickInRange ? 'Position in range' : 'Position out of range'}
          />
          <TextBody color="fgMuted" as="span" noWrap>
            {tickInRange ? 'In range' : 'Out of range'}
          </TextBody>
        </HStack>
      </TableCell>
    </TableRow>
  );
});
